
import { useState, FormEvent } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNewsletterSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d51sh1neagc3l4h0td0g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-gradient-to-br from-teal-600 to-teal-700 text-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex flex-col md:flex-row justify-between items-start mb-12">
          <div className="mb-6 md:mb-0">
            <div className="flex items-center space-x-3 mb-2">
              <img
                src="https://public.readdy.ai/ai/img_res/44f4e586-54b9-488d-8d2a-ce4b5a7e3494.png"
                alt="DINARCO S.R.L."
                className="h-10 w-10 object-contain"
              />
              <span className="text-2xl font-bold">DINARCO</span>
            </div>
            <p className="text-teal-100 text-sm">Your trusted VPN solution</p>
          </div>
          <p className="text-teal-100 text-sm">Securing connections worldwide since 2025</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div>
            <h4 className="font-bold text-lg mb-4">Company</h4>
            <ul className="space-y-3">
              <li>
                <button onClick={() => scrollToSection('about')} className="text-teal-100 hover:text-white transition-colors text-sm cursor-pointer">
                  About
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('services')} className="text-teal-100 hover:text-white transition-colors text-sm cursor-pointer">
                  Services
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('contact')} className="text-teal-100 hover:text-white transition-colors text-sm cursor-pointer">
                  Contact
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('support')} className="text-teal-100 hover:text-white transition-colors text-sm cursor-pointer">
                  Support
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-lg mb-4">Legal</h4>
            <ul className="space-y-3">
              <li>
                <a href="#" className="text-teal-100 hover:text-white transition-colors text-sm cursor-pointer">
                  Privacy Policy
                </a>
              </li>
              <li>
                <a href="#" className="text-teal-100 hover:text-white transition-colors text-sm cursor-pointer">
                  Terms of Service
                </a>
              </li>
              <li>
                <a href="#" className="text-teal-100 hover:text-white transition-colors text-sm cursor-pointer">
                  Cookie Policy
                </a>
              </li>
              <li>
                <a href="#" className="text-teal-100 hover:text-white transition-colors text-sm cursor-pointer">
                  Compliance
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-lg mb-4">Resources</h4>
            <ul className="space-y-3">
              <li>
                <button onClick={() => scrollToSection('faq')} className="text-teal-100 hover:text-white transition-colors text-sm cursor-pointer">
                  FAQ
                </button>
              </li>
              <li>
                <a href="#" className="text-teal-100 hover:text-white transition-colors text-sm cursor-pointer">
                  Documentation
                </a>
              </li>
              <li>
                <a href="#" className="text-teal-100 hover:text-white transition-colors text-sm cursor-pointer">
                  Blog
                </a>
              </li>
              <li>
                <a href="#" className="text-teal-100 hover:text-white transition-colors text-sm cursor-pointer">
                  Security
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 items-center mb-12">
          <div className="w-full h-32 rounded-2xl overflow-hidden">
            <img
              src="https://public.readdy.ai/ai/img_res/71ebb0fa210866fee26ddd04ced8b3d6.jpg"
              alt="Network Visualization"
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="text-center">
            <p className="text-2xl font-serif leading-relaxed">
              Protecting your<br />digital freedom<br />every day
            </p>
          </div>

          <div>
            <h4 className="font-bold mb-3">Stay Updated</h4>
            <form id="newsletter-form" data-readdy-form onSubmit={handleNewsletterSubmit} className="flex gap-2">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Your email"
                className="flex-1 px-4 py-2 rounded-lg bg-teal-800 border border-teal-600 text-white placeholder-teal-300 focus:outline-none focus:ring-2 focus:ring-white/30 text-sm"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-white text-teal-600 rounded-lg font-medium hover:bg-teal-50 transition-all flex items-center space-x-2 disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                <span className="text-sm">Subscribe</span>
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </button>
            </form>
            {submitStatus === 'success' && (
              <p className="text-xs text-teal-100 mt-2">Thank you for subscribing!</p>
            )}
            {submitStatus === 'error' && (
              <p className="text-xs text-red-200 mt-2">Something went wrong. Please try again.</p>
            )}
          </div>
        </div>

        <div className="border-t border-teal-500 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-teal-100 text-sm">© 2025 DINARCO S.R.L. All rights reserved</p>
            
            <div className="flex items-center space-x-6">
              <a href="#" className="text-teal-100 hover:text-white transition-colors cursor-pointer" aria-label="LinkedIn">
                <i className="ri-linkedin-fill text-xl w-5 h-5 flex items-center justify-center"></i>
              </a>
              <a href="#" className="text-teal-100 hover:text-white transition-colors cursor-pointer" aria-label="Twitter">
                <i className="ri-twitter-x-fill text-xl w-5 h-5 flex items-center justify-center"></i>
              </a>
              <a href="#" className="text-teal-100 hover:text-white transition-colors cursor-pointer" aria-label="Facebook">
                <i className="ri-facebook-fill text-xl w-5 h-5 flex items-center justify-center"></i>
              </a>
              <a href="#" className="text-teal-100 hover:text-white transition-colors cursor-pointer" aria-label="Instagram">
                <i className="ri-instagram-fill text-xl w-5 h-5 flex items-center justify-center"></i>
              </a>
            </div>
          </div>
          
          <div className="text-center mt-4">
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-teal-100 hover:text-white text-xs transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
